import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Button, Table, Card, Badge, Modal } from 'react-bootstrap';
import addIcon from '../assets/icons/add.svg';
import closeIcon from '../assets/icons/close.svg';
import { formatPaymentMethod } from '../utils/paymentMethodFormatter';
import { CustomerLink, TransactionLink, RTOLink } from './EntityLinks';
import { usePaginatedData } from '../hooks/usePaginatedData';
import { DeleteConfirmModal } from './shared/DeleteConfirmModal';
import { getAgentId } from '../utils/agentId';
import { useURLParams } from '../hooks/useURLParams';
import { useColumnConfig } from '../hooks/useColumnConfig';
import { ColumnConfigDropdown } from './shared/ColumnConfigDropdown';
import { ReconciliationView } from './ReconciliationView';
import { PaymentForm } from './PaymentForm';
import { formatCents, dollarsToCents } from '../utils/money';

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'customer', label: 'Customer' },
  { key: 'agent', label: 'Agent' },
  { key: 'transaction_rto', label: 'Transaction / Lease' },
  { key: 'amount', label: 'Amount' },
  { key: 'method', label: 'Method' },
  { key: 'date', label: 'Date' },
  { key: 'actions', label: 'Actions' },
];

/**
 * PaymentList component displays all payments in a table
 * and provides navigation to detail view and creation
 */
export function PaymentList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { apiCall, user } = useOutletContext();
  const [showNewPaymentForm, setShowNewPaymentForm] = useState(false);
  const [showMatchPayment, setShowMatchPayment] = useState(false);
  // URL-synced pagination
  const { getParam, setParam, setParams } = useURLParams({ page: 0 });
  const urlPage = getParam('page');

  // Column config from URL
  const { visibleColumns, isVisible, toggleColumn, allColumns } = useColumnConfig(COLUMNS);

  // Use paginated data hook
  const {
    data: filteredPayments,
    loading,
    error: loadError,
    pagination,
    reload,
    goToNextPage: _goToNextPage,
    goToPreviousPage: _goToPreviousPage,
  } = usePaginatedData(
    async (queryParams) => apiCall(`${API_BASE_URL}/payments?${queryParams}`),
    { limit: 50, dependencies: [apiCall], initialOffset: urlPage * 50 }
  );

  // Wrap pagination to sync URL
  const goToNextPage = useCallback(() => {
    _goToNextPage();
    const nextPage = Math.floor(pagination.offset / pagination.limit) + 1;
    setParam('page', nextPage, { replace: true });
  }, [_goToNextPage, pagination, setParam]);

  const goToPreviousPage = useCallback(() => {
    _goToPreviousPage();
    const prevPage = Math.floor(pagination.offset / pagination.limit) - 1;
    setParam('page', prevPage > 0 ? prevPage : '', { replace: true });
  }, [_goToPreviousPage, pagination, setParam]);

  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const deletePayment = (id) => {
    setDeleteTargetId(id);
    setDeleteError(null);
  };

  const confirmDeletePayment = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await apiCall(`${API_BASE_URL}/payments/${deleteTargetId}`, { method: 'DELETE' });
      setDeleteTargetId(null);
      reload();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  // Auto-open form if navigated from Quick Actions
  useEffect(() => {
    if (location.state?.openAddForm) {
      setShowNewPaymentForm(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Tender a payment. The shared PaymentForm validates and assembles the payload;
  // here we attach the agent and POST. Throw on failure so the form surfaces it.
  const handleCreatePayment = async (paymentData) => {
    const agentId = await getAgentId(apiCall, user);
    if (agentId === null) {
      throw new Error('Could not determine agent ID. Please refresh and try again.');
    }

    const response = await apiCall(`${API_BASE_URL}/payments`, {
      method: 'POST',
      body: JSON.stringify({ ...paymentData, agent_id: agentId }),
    });

    if (response && response.error) {
      throw new Error(response.error);
    }

    setShowNewPaymentForm(false);
    alert('Payment added successfully');
    reload();
  };

  // Use joined data from backend
  const getCustomerName = (payment) => {
    if (payment.first_name && payment.last_name) {
      return `${payment.first_name} ${payment.last_name}`;
    }
    return `Customer #${payment.customer_id}`;
  };

  const renderCell = (key, payment) => {
    switch (key) {
      case 'id': return payment.id;
      case 'customer':
        return (
          <CustomerLink
            customerId={payment.customer_id}
            customerName={getCustomerName(payment)}
            className="btn btn-link btn-sm p-0"
          />
        );
      case 'agent': return payment.agent_name || '-';
      case 'transaction_rto':
        if (payment.transaction_id) {
          return (
            <>
              <TransactionLink transactionId={payment.transaction_id} className="btn btn-link btn-sm p-0">
                #{payment.transaction_id}
              </TransactionLink>
              {payment.transaction_vehicle_make && (
                <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>
                  ({payment.transaction_vehicle_make} {payment.transaction_vehicle_model})
                </span>
              )}
            </>
          );
        }
        if (payment.rto_agreement_id) {
          return (
            <>
              <RTOLink rtoId={payment.rto_agreement_id} className="btn btn-link btn-sm p-0">
                Lease #{payment.rto_agreement_id}
              </RTOLink>
              {payment.rto_vehicle_make && (
                <span style={{ fontSize: '0.75rem', color: '#666', marginLeft: '0.5rem' }}>
                  ({payment.rto_vehicle_make} {payment.rto_vehicle_model})
                </span>
              )}
            </>
          );
        }
        return '(orphaned)';
      case 'amount': return formatCents(dollarsToCents(payment.amount));
      case 'method': return formatPaymentMethod(payment.payment_method);
      case 'date': return payment.payment_date ? payment.payment_date.split('T')[0] : '';
      case 'actions':
        return (
          <>
            <Button variant="link" size="sm" onClick={() => navigate(`./${payment.id}`)} className="me-2">
              View
            </Button>
            <Button variant="link" size="sm" className="text-danger" onClick={() => deletePayment(payment.id)}>
              Delete
            </Button>
          </>
        );
      default: return null;
    }
  };

  return (
    <Container fluid className="py-4 domain-payments">
      <Row className="mb-4">
        <Col>
          <h2>Payments</h2>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col>
          {user?.role !== 'auditor' && (
            <>
              <Button
                variant="primary"
                onClick={() => setShowMatchPayment(true)}
                className="me-2"
              >
                <img src={addIcon} alt="" className="icon-btn" />Match Payment
              </Button>
              <Button
                variant={showNewPaymentForm ? "secondary" : "outline-primary"}
                onClick={() => setShowNewPaymentForm(!showNewPaymentForm)}
              >
                {showNewPaymentForm ? <><img src={closeIcon} alt="" className="icon-btn" />Cancel</> : 'Tender Payment'}
              </Button>
            </>
          )}
        </Col>
        <Col xs="auto">
          <ColumnConfigDropdown allColumns={allColumns} isVisible={isVisible} toggleColumn={toggleColumn} />
        </Col>
      </Row>

      {showNewPaymentForm && (
        <Card className="mb-4">
          <Card.Header className="text-white" style={{ backgroundColor: '#f59e0b' }}>
            <h3>Tender Payment</h3>
          </Card.Header>
          <Card.Body>
            <PaymentForm
              apiCall={apiCall}
              remoteCandidates
              onSubmit={handleCreatePayment}
              onCancel={() => setShowNewPaymentForm(false)}
              submitLabel="Tender Payment"
            />
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Body className="p-0">
          {filteredPayments.length > 0 && (
            <div className="px-3 py-2 bg-light">
              <small className="text-muted">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} payments
              </small>
            </div>
          )}

          <Table striped hover responsive>
            <thead>
              <tr>
                {visibleColumns.map(col => (
                  <th key={col.key} className={col.className}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPayments.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} className="text-center py-4">
                    No payments found
                  </td>
                </tr>
              ) : (
                filteredPayments.map(payment => (
                  <tr key={payment.id}>
                    {visibleColumns.map(col => (
                      <td key={col.key} className={col.className}>{renderCell(col.key, payment)}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {filteredPayments.length > 0 && pagination.total > pagination.limit && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
          <Button
            variant="secondary"
            onClick={goToPreviousPage}
            disabled={pagination.offset === 0}
          >
            &larr; Previous
          </Button>
          <span className="text-muted">
            Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.ceil(pagination.total / pagination.limit)}
          </span>
          <Button
            variant="secondary"
            onClick={goToNextPage}
            disabled={pagination.offset + pagination.limit >= pagination.total}
          >
            Next &rarr;
          </Button>
        </div>
      )}

      {/* Match Payment Modal */}
      <Modal show={showMatchPayment} onHide={() => setShowMatchPayment(false)} size="xl" dialogClassName="modal-90w">
        <Modal.Header closeButton>
          <Modal.Title>Match Payment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReconciliationView apiCall={apiCall} user={user} />
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={!!deleteTargetId}
        onHide={() => setDeleteTargetId(null)}
        onConfirm={confirmDeletePayment}
        itemLabel="payment"
        error={deleteError}
        isDeleting={isDeleting}
      />
    </Container>
  );
}

export default PaymentList;
